import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import ReactApexChart from 'react-apexcharts';
import ChartErrorBoundary from './ChartErrorBoundary';
import { addDays, addHours, cloneDatetTime, getLocalShortHour } from '../utils/datetimeUtils';
import { xAxisFormatter } from '../utils/chartUtils';
import { useTheme } from '@emotion/react';


export default function CarCharging({
  beginingDate = addDays(new Date(), -1),
  endDate = new Date(),
}) {

  const theme = useTheme();


  const colorPalette = [
    '#2196F3', // Blue
    '#4CAF50', // Green
    '#FFC107', // Amber
    '#E91E63', // Pink
  ];

  const now = new Date();
  let temp = cloneDatetTime(beginingDate);
  const mockData = new Array(4);
  let empptyIndex = 20;
  while (temp.getTime() < endDate.getTime()) {
    for (let i = 0; i < mockData.length; i++) {
      const diff = empptyIndex < 0 ? Math.round(Math.random()) + 0.1 : 0;
      empptyIndex--;
      if (!mockData[i]) {
        mockData[i] = [];
      }
      mockData[i].push({
        date: temp,
        value: diff
      });
    }
    temp = addHours(cloneDatetTime(temp), 1);

  }


  // Validate and transform the data
  const CHARGING_DISCHARCING = React.useMemo(() => {
    try {
      return mockData.map((carCharging, index) => ({
        name: 'Car ' + (index + 1),
        type: 'area',
        data: carCharging
          .filter(d => d && d.date && !isNaN(d.date.getTime()) && typeof d.value === 'number')
          .map(d => ({
            x: d.date.getTime(),
            y: Number(d.value.toFixed(2))
          })),
        color: colorPalette[index],
        fillColor: colorPalette[index]
      })).filter(series => series.data.length > 0);
    } catch (error) {
      // If there's an error processing the data, return empty array
      return [];
    }
  }, [mockData, colorPalette]);

  // Removed unused xaxisConfig as it's now part of the chart options
  const annotations = {
    xaxis: [
      {
        x: now.getTime(),
        strokeDashArray: 5,
        // borderColor: "#FF9999", // Red color
        opacity: 0.5,
        label: {
          orientation: 'horizontal',
          position: "top",
          text: getLocalShortHour(now),
          offsetY: -10,
          style: {
            color: "#fff",
            background: "#FF9999",
          },
        },
      },
    ],
  };
  
  const coChargingDischarging = {
    annotations,
    chart: {
      type: "area",
      stacked: true,
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    stroke: {
      curve: "straight",
      width: 2,
      lineCap: 'square'
    },
    fill: {
      type: 'solid',
      opacity: 0.3
    },
    dataLabels: {
      enabled: false
    },
    xaxis: {
      type: "datetime",
      min: beginingDate.getTime(),
      max: endDate.getTime(),
      tickAmount: 10,
      labels: {
        formatter: xAxisFormatter,
        style: {
          colors: theme.palette.text.secondary
        }
      },
      axisBorder: {
        show: true,
        color: theme.palette.divider
      },
      axisTicks: {
        show: true,
        color: theme.palette.divider
      }
    },
    yaxis: {
      labels: {
        formatter: (value) => {
          if (typeof value !== 'undefined' && value !== null) {
            return `${value.toFixed(0)} kW`;
          }
          return value;
        },
        style: {
          colors: theme.palette.text.secondary
        }
      },
      title: {
        text: 'Charging & Discharging (kW)',
        style: {
          color: theme.palette.text.primary
        }
      }
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true
        }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => {
          if (typeof value !== 'undefined' && value !== null) {
            return `${value.toFixed(0)} kW`;
          }
          return value;
        }
      },
      x: {
        format: 'dd MMM yyyy HH:mm'
      }
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      labels: {
        colors: theme.palette.text.primary
      }
    }
  };

  // Check if we have valid data
  const hasValidData = CHARGING_DISCHARCING && CHARGING_DISCHARCING.length > 0 && 
    CHARGING_DISCHARCING.some(series => series.data && series.data.length > 0);

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Car Charging
        </Typography>
        <Stack sx={{ justifyContent: 'space-between' }}>
          {hasValidData ? (
            <ChartErrorBoundary>
              <ReactApexChart 
                type="area" 
                series={CHARGING_DISCHARCING} 
                options={coChargingDischarging} 
                height={220} 
              />
            </ChartErrorBoundary>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No charging data available for the selected period
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
